import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';
import useOperationalStore from '../platform/store/useOperationalStore';
import {
  DEFAULT_LOCATION_ID,
  EVENT_TYPES,
  nowSeconds,
  recordOperationEvent,
} from '../platform/events/operationEvents';
import { getCurrentRole, hasPermission, PERMISSIONS } from '../shared/permissions';
import {
  ROAD_STATUS,
  ROAD_STATUS_LABELS,
  ROAD_HOLD_REASONS,
  availableRoadTransitions,
  canonicalRoadStatus,
  isRoadCallActive,
  roadHoldReasonLabel,
  roadTransitionNote,
  summarizeRoadResponse,
} from '../shared/domain';
import {
  buildDispatchTransition,
  fetchRoadStatusEvents,
  writeRoadStatusEvent,
} from '../platform/roadService/roadStatusEvents';
import DispatchMap from '../components/DispatchMap/DispatchMap';
import FullscreenBtn from '../components/FullscreenBtn';
import NewROModal from '../components/Modals/NewROModal';
import './RoadServiceView.css';

const client = generateClient();

// Which statuses count as active now comes from the shared machine
// (isRoadCallActive), so HOLD and any future state are covered automatically.

const EMPTY_CALL_FORM = {
  customerName: '',
  companyName: '',
  contactName: '',
  complaint: '',
  roadServiceAddress: '',
  callerPhone: '',
  callbackPhone: '',
  unitNumber: '',
  vin: '',
  vehicleYear: '',
  vehicleMake: '',
  vehicleModel: '',
  vehicleColor: '',
  priority: 'NORMAL',
  assignedTechnicianId: '',
  destinationLatitude: '',
  destinationLongitude: '',
};
const DEFAULT_GEOCODE_REGION = 'Albany NY';

// The status buttons come from the shared machine — the same one the native app
// runs — so the board can't offer a move the tech's phone would refuse.
//
// Dispatch sees one button the tech doesn't: Accept-on-behalf. A tech whose
// phone is dead still has to get moving, and someone has to be able to say so.
const DISPATCH_TRANSITION_LABELS = {
  [ROAD_STATUS.ASSIGNED]: 'Accept for Tech',
  [ROAD_STATUS.HOLD]: 'Hold',
};

function dispatchTransitionLabel(call, next) {
  if (canonicalRoadStatus(call.status) === ROAD_STATUS.HOLD) {
    return `Resume ${ROAD_STATUS_LABELS[next] || next}`;
  }
  return DISPATCH_TRANSITION_LABELS[next] || ROAD_STATUS_LABELS[next] || next;
}

// Cancel is rendered separately (it's destructive and always last), so keep it
// out of the normal button run.
//
// Accepting on behalf of a tech requires there to BE a tech: an ASSIGNED call
// with nobody attached is how calls used to go missing. Pick someone in the
// dropdown first.
function dispatchTransitions(call) {
  return availableRoadTransitions(call)
    .filter(next => next !== ROAD_STATUS.CANCELLED)
    .filter(next => next !== ROAD_STATUS.ASSIGNED || !!call.assignedTechnicianId);
}

// The legacy activity stream keeps its own vocabulary; map into it so the
// history panels that already read OperationEvent don't go blank.
function statusEventType(status) {
  switch (canonicalRoadStatus(status)) {
    case ROAD_STATUS.ASSIGNED:  return EVENT_TYPES.ASSIGNED;
    case ROAD_STATUS.EN_ROUTE:  return EVENT_TYPES.TRAVELING;
    case ROAD_STATUS.ON_SCENE:  return EVENT_TYPES.ARRIVED;
    case ROAD_STATUS.HOLD:      return EVENT_TYPES.HOLD;
    case ROAD_STATUS.COMPLETE:  return EVENT_TYPES.COMPLETED;
    case ROAD_STATUS.CANCELLED: return EVENT_TYPES.CANCELLED;
    default: return EVENT_TYPES.STATUS_CHANGED;
  }
}

function formatDuration(seconds) {
  if (!Number.isFinite(seconds)) return '—';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
}

// Haversine straight-line distance in miles
function distanceMiles(lat1, lon1, lat2, lon2) {
  const R = 3958.8;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function mapsUrl(lat, lng, address) {
  if (lat && lng) return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  if (address) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  return null;
}

function makeLocationToken() {
  const bytes = new Uint8Array(16);
  window.crypto?.getRandomValues?.(bytes);
  const random = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return random || `${Date.now()}${Math.random().toString(36).slice(2)}`;
}

function publicAppOrigin() {
  const configured = process.env.REACT_APP_PUBLIC_APP_URL || window.localStorage?.getItem('rekon_public_app_url');
  return String(configured || window.location.origin).replace(/\/+$/, '');
}

function locationRequestUrl(token) {
  return `${publicAppOrigin()}/road-location/${encodeURIComponent(token)}`;
}

function formatTime(value) {
  if (!value) return '—';
  const date = /^\d+$/.test(String(value)) ? new Date(Number(value) * 1000) : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function statusTone(status) {
  const canonical = canonicalRoadStatus(status);
  if (canonical === ROAD_STATUS.COMPLETE) return 'completed';
  if (canonical === ROAD_STATUS.HOLD) return 'waiting';
  if (canonical === ROAD_STATUS.CANCELLED) return 'cancelled';
  return 'active';
}

function statusLabel(status) {
  return ROAD_STATUS_LABELS[canonicalRoadStatus(status)] || String(status || '').replace(/_/g, ' ');
}

function belongsToActiveLocation(call) {
  return !call?.locationId || call.locationId === DEFAULT_LOCATION_ID;
}

async function geocodeRoadServiceAddress(address) {
  const cleaned = String(address || '').trim();
  if (!cleaned) return null;
  const hasLocality = /,\s*\w+|(?:\b[A-Z]{2}\b)|\b\d{5}(?:-\d{4})?\b/.test(cleaned);
  const query = hasLocality ? cleaned : `${cleaned}, ${DEFAULT_GEOCODE_REGION}`;
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`;
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) return null;
  const data = await res.json();
  const hit = data?.[0];
  if (!hit?.lat || !hit?.lon) return null;
  return { latitude: Number(hit.lat), longitude: Number(hit.lon) };
}

export default function RoadServiceView() {
  const navigate       = useNavigate();
  const technicians    = useOperationalStore(s => s.technicians);
  const localAddTruck  = useOperationalStore(s => s.localAddTruck);
  const [calls, setCalls]             = useState([]);
  const [techLocations, setTechLocs]  = useState([]);
  const [eventsByCall, setEventsByCall] = useState({});
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [geocodingId, setGeocodingId] = useState(null);
  const [filter, setFilter]           = useState('active');
  const [createOpen, setCreateOpen]   = useState(false);
  const [convertCall, setConvertCall] = useState(null); // road call → RO modal
  const [selectedCall, setSelectedCall] = useState(null);
  const [holdTarget, setHoldTarget]   = useState(null); // { call, eventType }
  // Transition history for the open drawer only — the board doesn't need every
  // call's history, and one query per selection is cheaper than N on every load.
  const [statusEvents, setStatusEvents] = useState({ callId: null, items: [] });
  const [draftCallId, setDraftCallId] = useState(null);
  const [requestingLocation, setRequestingLocation] = useState(false);
  const [publicUrlDraft, setPublicUrlDraft] = useState(() =>
    window.localStorage?.getItem('rekon_public_app_url') || ''
  );
  const [form, setForm]               = useState(EMPTY_CALL_FORM);

  // ── Load calls ──────────────────────────────────────────────────────────────
  async function loadCalls({ quiet = false } = {}) {
    if (!quiet) setLoading(true);
    try {
      const [byLoc, all] = await Promise.all([
        client.graphql({ query: queries.listRoadServiceCalls, variables: { limit: 200, filter: { locationId: { eq: DEFAULT_LOCATION_ID } } } }),
        client.graphql({ query: queries.listRoadServiceCalls, variables: { limit: 200 } }),
      ]);
      const byId = new Map();
      [
        ...(byLoc.data?.listRoadServiceCalls?.items || []),
        ...(all.data?.listRoadServiceCalls?.items || []).filter(belongsToActiveLocation),
      ].forEach(call => byId.set(call.id, call));
      setCalls([...byId.values()]);
    } catch (err) {
      console.error('Road service load failed:', err);
    } finally {
      if (!quiet) setLoading(false);
    }
  }

  // ── Load tech locations (graceful — model may not be deployed yet) ──────────
  async function loadTechLocations() {
    if (!queries.listTechnicianLocations) return;
    try {
      const res = await client.graphql({
        query: queries.listTechnicianLocations,
        variables: { limit: 100, filter: { locationId: { eq: DEFAULT_LOCATION_ID } } },
      });
      const items = res.data?.listTechnicianLocations?.items || [];
      // Keep only most-recent record per technician
      const byTech = new Map();
      items.forEach(loc => {
        const existing = byTech.get(loc.technicianId);
        if (!existing || loc.timestamp > existing.timestamp) byTech.set(loc.technicianId, loc);
      });
      setTechLocs([...byTech.values()]);
    } catch {
      // TechnicianLocation not yet deployed — map shows calls only
    }
  }

  useEffect(() => {
    loadCalls();
    loadTechLocations();
    const interval = setInterval(() => {
      loadCalls({ quiet: true });
      loadTechLocations();
    }, 30_000);
    return () => clearInterval(interval);
  }, []);

  // Pull the transition history for whichever call is open in the drawer.
  useEffect(() => {
    const callId = selectedCall?.id;
    if (!callId) {
      setStatusEvents({ callId: null, items: [] });
      return undefined;
    }
    let cancelled = false;
    fetchRoadStatusEvents(callId).then(items => {
      if (!cancelled) setStatusEvents({ callId, items });
    });
    return () => { cancelled = true; };
  }, [selectedCall?.id]);

  // ── Derived state ───────────────────────────────────────────────────────────
  const visible = useMemo(() => {
    const list = calls.filter(call => {
      if (filter === 'all')    return true;
      if (filter === 'closed') return !isRoadCallActive(call);
      return isRoadCallActive(call);
    });
    return [...list].sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
  }, [calls, filter]);

  const activeCount    = calls.filter(isRoadCallActive).length;
  const completedCount = calls.filter(c => canonicalRoadStatus(c.status) === ROAD_STATUS.COMPLETE).length;
  const incomingCount  = calls.filter(c => canonicalRoadStatus(c.status) === ROAD_STATUS.INCOMING).length;
  const onHoldCount    = calls.filter(c => canonicalRoadStatus(c.status) === ROAD_STATUS.HOLD).length;
  const canManage      = hasPermission(getCurrentRole(), PERMISSIONS.MANAGE_ROAD_SERVICE);
  const linkOrigin      = publicAppOrigin();
  const linkIsLocal     = /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])/i.test(linkOrigin);

  function savePublicUrl() {
    const cleanUrl = publicUrlDraft.trim().replace(/\/+$/, '');
    if (!/^https:\/\/[^ ]+\.[^ ]+/.test(cleanUrl)) {
      alert('Enter the deployed public app URL, like https://your-app.vercel.app');
      return;
    }
    window.localStorage?.setItem('rekon_public_app_url', cleanUrl);
    window.location.reload();
  }

  async function pinCallFromAddress(call) {
    if (!call?.roadServiceAddress || geocodingId) return false;
    setGeocodingId(call.id);
    try {
      const coords = await geocodeRoadServiceAddress(call.roadServiceAddress);
      if (!coords) {
        alert('Could not find coordinates for that address. Add city/state or enter exact latitude and longitude.');
        return false;
      }
      await client.graphql({
        query: mutations.updateRoadServiceCall,
        variables: {
          input: {
            id: call.id,
            destinationLatitude: coords.latitude,
            destinationLongitude: coords.longitude,
          },
        },
      });
      setCalls(prev => prev.map(item => item.id === call.id
        ? { ...item, destinationLatitude: coords.latitude, destinationLongitude: coords.longitude }
        : item
      ));
      return true;
    } catch (err) {
      console.warn('Road service pin from address failed:', err.message);
      alert('Could not pin that address right now.');
      return false;
    } finally {
      setGeocodingId(null);
    }
  }

  async function sendIntakeLocationRequest() {
    if (!canManage || requestingLocation) return;
    const cleanPhone = form.callerPhone.trim();
    if (!cleanPhone) {
      alert('Enter the caller phone number first.');
      return;
    }
    setRequestingLocation(true);
    const token = makeLocationToken();
    const requestedAt = nowSeconds();
    const url = locationRequestUrl(token);
    const message = `REKON road service needs your location. Tap this link and press Send My Location: ${url}`;

    try {
      const callbackPhone = form.callbackPhone.trim() || cleanPhone;
      let call;
      if (draftCallId) {
        const result = await client.graphql({
          query: mutations.updateRoadServiceCall,
          variables: {
            input: {
              id: draftCallId,
              customerName: form.customerName.trim() || null,
              companyName: form.companyName.trim() || null,
              contactName: form.contactName.trim() || null,
              complaint: form.complaint.trim() || 'Location request pending',
              roadServiceAddress: form.roadServiceAddress.trim() || null,
              callbackPhone,
              unitNumber: form.unitNumber.trim() || null,
              vin: form.vin.trim() || null,
              vehicleYear: form.vehicleYear.trim() || null,
              vehicleMake: form.vehicleMake.trim() || null,
              vehicleModel: form.vehicleModel.trim() || null,
              vehicleColor: form.vehicleColor.trim() || null,
              priority: form.priority,
              assignedTechnicianId: form.assignedTechnicianId || null,
              status: form.assignedTechnicianId ? 'ASSIGNED' : 'OPEN',
              locationRequestToken: token,
              locationRequestPhone: cleanPhone,
              locationRequestedAt: requestedAt,
              locationRequestStatus: 'SENT',
            },
          },
        });
        call = result.data?.updateRoadServiceCall;
      } else {
        const result = await client.graphql({
          query: mutations.createRoadServiceCall,
          variables: {
            input: {
              locationId: DEFAULT_LOCATION_ID,
              customerName: form.customerName.trim() || null,
              companyName: form.companyName.trim() || null,
              contactName: form.contactName.trim() || null,
              complaint: form.complaint.trim() || 'Location request pending',
              roadServiceAddress: form.roadServiceAddress.trim() || null,
              callbackPhone,
              unitNumber: form.unitNumber.trim() || null,
              vin: form.vin.trim() || null,
              vehicleYear: form.vehicleYear.trim() || null,
              vehicleMake: form.vehicleMake.trim() || null,
              vehicleModel: form.vehicleModel.trim() || null,
              vehicleColor: form.vehicleColor.trim() || null,
              priority: form.priority,
              assignedTechnicianId: form.assignedTechnicianId || null,
              // Picking a tech dispatches the call AT them; it stays OPEN until
              // that tech accepts it on their phone. ASSIGNED means a human has
              // it, and the gap between the two is the acknowledgement time
              // fleet customers ask about.
              status: 'OPEN',
              dispatchedAt: requestedAt,
              locationRequestToken: token,
              locationRequestPhone: cleanPhone,
              locationRequestedAt: requestedAt,
              locationRequestStatus: 'SENT',
            },
          },
        });
        call = result.data?.createRoadServiceCall;
        if (call?.id) setDraftCallId(call.id);
      }

      if (call) {
        setCalls(prev => {
          const exists = prev.some(item => item.id === call.id);
          return exists ? prev.map(item => item.id === call.id ? call : item) : [call, ...prev];
        });
      }

      await recordOperationEvent({
        eventType: EVENT_TYPES.STATUS_CHANGED,
        timestamp: requestedAt,
        locationId: call?.locationId || DEFAULT_LOCATION_ID,
        assetId: call?.assetId || null,
        workOrderId: call?.workOrderId || call?.id || null,
        technicianId: call?.assignedTechnicianId || null,
        userId: null,
        previousStatus: call?.status || null,
        newStatus: call?.status || null,
        note: `Location request sent to ${cleanPhone}`,
        relatedPhotoIds: [],
        metadata: JSON.stringify({ source: 'road-service-intake', roadServiceCallId: call?.id || null }),
      });

      try {
        await navigator.clipboard?.writeText(message);
      } catch {}

      window.location.href = `sms:${encodeURIComponent(cleanPhone)}?&body=${encodeURIComponent(message)}`;
    } catch (err) {
      console.warn('Location request failed:', err.message);
      alert('Could not create location request link.');
    } finally {
      setRequestingLocation(false);
    }
  }

  useEffect(() => {
    if (!canManage) return;
    let cancelled = false;

    async function backfillMissingPins() {
      const candidates = calls
        .filter(isRoadCallActive)
        .filter(call => call.roadServiceAddress && !call.destinationLatitude && !call.destinationLongitude)
        .slice(0, 6);

      for (const call of candidates) {
        if (cancelled) return;
        try {
          const coords = await geocodeRoadServiceAddress(call.roadServiceAddress);
          if (!coords || cancelled) continue;
          await client.graphql({
            query: mutations.updateRoadServiceCall,
            variables: {
              input: {
                id: call.id,
                destinationLatitude: coords.latitude,
                destinationLongitude: coords.longitude,
              },
            },
          });
          setCalls(prev => prev.map(item => item.id === call.id
            ? { ...item, destinationLatitude: coords.latitude, destinationLongitude: coords.longitude }
            : item
          ));
        } catch (err) {
          console.warn('Road service geocode backfill failed:', err.message);
        }
      }
    }

    backfillMissingPins();
    return () => { cancelled = true; };
  }, [calls, canManage]);

  // ── Load events for visible calls ──────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!visible.length || !queries.operationEventsByWorkOrder) { setEventsByCall({}); return; }
      const results = await Promise.all(visible.slice(0, 40).map(async call => {
        try {
          const res = await client.graphql({
            query: queries.operationEventsByWorkOrder,
            variables: { workOrderId: call.workOrderId || call.id, sortDirection: 'DESC', limit: 30 },
          });
          return [call.id, res.data?.operationEventsByWorkOrder?.items || []];
        } catch { return [call.id, []]; }
      }));
      if (!cancelled) setEventsByCall(Object.fromEntries(results));
    }
    load();
    return () => { cancelled = true; };
  }, [visible]);

  // ── Create call ─────────────────────────────────────────────────────────────
  async function createCall(e) {
    e.preventDefault();
    if (!canManage) return;
    const complaint = form.complaint.trim();
    const address = form.roadServiceAddress.trim();
    if (!complaint || saving) return;
    setSaving(true);
    try {
      let lat = form.destinationLatitude ? parseFloat(form.destinationLatitude) : null;
      let lng = form.destinationLongitude ? parseFloat(form.destinationLongitude) : null;
      if ((!Number.isFinite(lat) || !Number.isFinite(lng)) && address) {
        const coords = await geocodeRoadServiceAddress(address);
        if (coords) {
          lat = coords.latitude;
          lng = coords.longitude;
        }
      }
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        const draft = draftCallId ? calls.find(call => call.id === draftCallId) : null;
        if (draft?.destinationLatitude != null && draft?.destinationLongitude != null) {
          lat = draft.destinationLatitude;
          lng = draft.destinationLongitude;
        }
      }
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        alert('Enter a customer road-service address or exact coordinates so dispatch can place this call on the map.');
        setSaving(false);
        return;
      }
      // Editing a call that's already past dispatch must not reset its status.
      // Otherwise: a new call is OPEN even when dispatch names the tech — the
      // tech accepting it is what makes it ASSIGNED — and an already-accepted
      // call keeps that acceptance unless dispatch pulls the tech off it, which
      // puts it back in the incoming pool for someone else to take.
      const existingCall = draftCallId ? calls.find(item => item.id === draftCallId) : null;
      const pastDispatch = existingCall && !['OPEN', 'ASSIGNED'].includes(existingCall.status);
      const nextStatus = pastDispatch
        ? existingCall.status
        : (existingCall?.status === 'ASSIGNED' && form.assignedTechnicianId ? 'ASSIGNED' : 'OPEN');

      const input = {
        ...(draftCallId ? { id: draftCallId } : { locationId: DEFAULT_LOCATION_ID }),
        customerName: form.customerName.trim() || null,
        companyName: form.companyName.trim() || null,
        contactName: form.contactName.trim() || null,
        complaint,
        roadServiceAddress: address || null,
        destinationLatitude: lat,
        destinationLongitude: lng,
        callbackPhone: form.callbackPhone.trim() || form.callerPhone.trim() || null,
        unitNumber: form.unitNumber.trim() || null,
        vin: form.vin.trim() || null,
        vehicleYear: form.vehicleYear.trim() || null,
        vehicleMake: form.vehicleMake.trim() || null,
        vehicleModel: form.vehicleModel.trim() || null,
        vehicleColor: form.vehicleColor.trim() || null,
        priority: form.priority,
        assignedTechnicianId: form.assignedTechnicianId || null,
        status: nextStatus,
        locationRequestPhone: form.callerPhone.trim() || null,
      };
      const result = await client.graphql({
        query: draftCallId ? mutations.updateRoadServiceCall : mutations.createRoadServiceCall,
        variables: { input },
      });
      const call = draftCallId ? result.data?.updateRoadServiceCall : result.data?.createRoadServiceCall;
      if (call) {
        setCalls(prev => {
          const exists = prev.some(item => item.id === call.id);
          return exists ? prev.map(item => item.id === call.id ? call : item) : [call, ...prev];
        });
        await recordOperationEvent({
          eventType: draftCallId ? EVENT_TYPES.STATUS_CHANGED : EVENT_TYPES.CALL_CREATED,
          timestamp: nowSeconds(),
          locationId: call.locationId,
          assetId: call.assetId || null,
          workOrderId: call.workOrderId || call.id,
          technicianId: call.assignedTechnicianId || null,
          userId: null,
          previousStatus: null,
          newStatus: call.status,
          note: call.complaint,
          relatedPhotoIds: [],
          metadata: JSON.stringify({ source: 'road-service-dispatch', roadServiceCallId: call.id, address: call.roadServiceAddress, priority: call.priority }),
        });
      }
      setForm(EMPTY_CALL_FORM);
      setDraftCallId(null);
      setCreateOpen(false);
    } catch (err) {
      console.error('Road service create failed:', err);
      alert('Could not create road service call.');
    } finally {
      setSaving(false);
    }
  }

  // ── Assign / reassign a tech on an existing call ────────────────────────────
  async function assignTech(call, technicianId) {
    if (!canManage || saving) return;
    setSaving(true);
    try {
      // Picking a tech dispatches the call AT them and nothing more. It stays
      // OPEN until that tech accepts it on their phone (or dispatch accepts on
      // their behalf), because the gap between the two IS the acknowledgement
      // time the customer is asking about.
      const input = { id: call.id, assignedTechnicianId: technicianId || null };
      const result = await client.graphql({
        query: mutations.updateRoadServiceCall,
        variables: { input },
      });
      const updated = result.data?.updateRoadServiceCall;
      if (updated) {
        setCalls(prev => prev.map(item => item.id === call.id ? updated : item));
        if (selectedCall?.id === call.id) setSelectedCall(updated);
      }
      if (technicianId) {
        await recordOperationEvent({
          eventType: EVENT_TYPES.ASSIGNED,
          timestamp: nowSeconds(),
          locationId: call.locationId || DEFAULT_LOCATION_ID,
          assetId: call.assetId || null,
          workOrderId: call.workOrderId || call.id,
          technicianId,
          userId: null,
          previousStatus: call.status,
          newStatus: input.status || call.status,
          note: call.complaint,
          relatedPhotoIds: [],
          metadata: JSON.stringify({ source: 'road-service-dispatch', roadServiceCallId: call.id }),
        });
        setEventsByCall(prev => ({
          ...prev,
          [call.id]: [{ eventType: EVENT_TYPES.ASSIGNED, timestamp: nowSeconds(), id: `local-${call.id}-${Date.now()}` }, ...(prev[call.id] || [])].slice(0, 5),
        }));
      }
    } catch (err) {
      console.error('Road service tech assignment failed:', err);
      alert('Could not assign the technician.');
    } finally {
      setSaving(false);
    }
  }

  // ── Edit an existing call: reuse the intake slide-over ─────────────────────
  function editCall(call) {
    setForm({
      customerName: call.customerName || '',
      companyName: call.companyName || '',
      contactName: call.contactName || '',
      complaint: call.complaint || '',
      roadServiceAddress: call.roadServiceAddress || '',
      callerPhone: call.locationRequestPhone || '',
      callbackPhone: call.callbackPhone || '',
      unitNumber: call.unitNumber || '',
      vin: call.vin || '',
      vehicleYear: call.vehicleYear || '',
      vehicleMake: call.vehicleMake || '',
      vehicleModel: call.vehicleModel || '',
      vehicleColor: call.vehicleColor || '',
      priority: call.priority || 'NORMAL',
      assignedTechnicianId: call.assignedTechnicianId || '',
      destinationLatitude: call.destinationLatitude != null ? String(call.destinationLatitude) : '',
      destinationLongitude: call.destinationLongitude != null ? String(call.destinationLongitude) : '',
    });
    setDraftCallId(call.id);
    setSelectedCall(null);
    setCreateOpen(true);
  }

  // ── Convert a road call into a repair order ─────────────────────────────────
  // Pre-fills the New RO modal from the call; on create, links the call via
  // workOrderId so it can't be converted twice and the RO is one click away.

  function roPrefillFromCall(call) {
    const tech = technicians.find(t => t.id === call.assignedTechnicianId);
    return {
      companyName:   call.companyName || call.customerName || '',
      truckNumber:   call.unitNumber || '',
      contactPerson: call.contactName || call.customerName || '',
      phone:         call.callbackPhone || '',
      year:          call.vehicleYear || '',
      make:          call.vehicleMake || '',
      model:         call.vehicleModel || '',
      vin:           call.vin || '',
      reason:        call.complaint || '',
      techNumber:    tech?.number || '',
      initialNote:   `Converted from road service call${call.roadServiceAddress ? ` at ${call.roadServiceAddress}` : ''}.`,
    };
  }

  async function handleConvertSubmit(truckData) {
    const call = convertCall;
    if (!call) return;
    const currentTime = nowSeconds();

    const activities = [];
    if (truckData.initialNote?.trim()) {
      activities.push({
        id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        type: 'note',
        text: truckData.initialNote.trim(),
        author: getCurrentRole(),
        timestamp: currentTime,
        photos: [],
        customerFacing: false,
        category: null,
        stage: 'intake',
      });
    }
    const techNumbers = truckData.techNumber ? [String(truckData.techNumber)] : [];

    try {
      const result = await client.graphql({
        query: mutations.createTruck,
        variables: {
          input: {
            repairOrder: truckData.repairOrder,
            locationId: DEFAULT_LOCATION_ID,
            companyName: truckData.companyName,
            truckNumber: truckData.truckNumber || '',
            reason: truckData.reason || null,
            contactPerson: truckData.contactPerson || '',
            phone: truckData.phone || '',
            email: truckData.email || '',
            partsHandling: truckData.partsHandling || '',
            customerCheckInId: null,
            year: truckData.year || '',
            make: truckData.make || '',
            model: truckData.model || '',
            vin: truckData.vin || '',
            mileage: truckData.mileage || '',
            hours: truckData.hours || '',
            faults: truckData.faults || '',
            customerType: truckData.customerType || null,
            poNumber: truckData.poNumber || null,
            status: 'yard',
            position: 0,
            warranty: truckData.warranty || false,
            promisedTime: truckData.promisedTime || null,
            techNumbers: techNumbers.length ? techNumbers : null,
            activities: activities.length ? activities : null,
            startTime: currentTime,
            lastStatusChange: currentTime,
            lastNote: currentTime,
            timeInWip: 0,
            wipStartTime: null,
            isPaused: false,
            notes: [],
            statusHistory: [{ status: 'yard', timeEntered: currentTime }],
            estimatedTime: 0,
            expired: false,
            warning: false,
            critical: false,
          },
        },
      });
      const newTruck = result?.data?.createTruck;
      if (!newTruck?.id) throw new Error('createTruck returned no id');

      localAddTruck(newTruck); // board shows it instantly (pendingAdds shield)

      await client.graphql({
        query: mutations.updateRoadServiceCall,
        variables: { input: { id: call.id, workOrderId: newTruck.id } },
      });
      setCalls(prev => prev.map(item => item.id === call.id ? { ...item, workOrderId: newTruck.id } : item));
      if (selectedCall?.id === call.id) setSelectedCall(prev => ({ ...prev, workOrderId: newTruck.id }));
      setConvertCall(null);
    } catch (err) {
      console.error('Road call conversion failed:', err);
      alert(`Could not create the RO: ${err?.errors?.[0]?.message || err.message || 'unknown error'}.`);
    }
  }

  // ── Update call status ──────────────────────────────────────────────────────
  // Holds need a reason before anything is written, so a Hold button opens the
  // dialog and comes back through here once the reason is picked.
  function requestStatusUpdate(call, nextStatus, eventType) {
    if (nextStatus === ROAD_STATUS.HOLD) {
      setHoldTarget({ call, eventType });
      return;
    }
    updateCallStatus(call, nextStatus, eventType);
  }

  async function updateCallStatus(call, nextStatus, eventType, hold = {}) {
    if (!canManage || saving) return;
    setSaving(true);
    try {
      const technician = technicians.find(t => t.id === call.assignedTechnicianId) || null;
      const { at, callPatch, statusEvent } = buildDispatchTransition({
        call,
        next: nextStatus,
        technician,
        holdReason: hold.holdReason || null,
        holdNote: hold.holdNote || null,
      });

      // History first: the current status can be re-derived from the board, but
      // a missing transition is a hole in the customer's response-time report.
      await writeRoadStatusEvent(statusEvent);

      const result = await client.graphql({
        query: mutations.updateRoadServiceCall,
        variables: { input: callPatch },
      });
      const updated = result.data?.updateRoadServiceCall || { ...call, ...callPatch };
      setCalls(prev => prev.map(item => item.id === call.id ? updated : item));
      if (selectedCall?.id === call.id) setSelectedCall(updated);
      if (statusEvents.callId === call.id) {
        setStatusEvents(prev => ({ ...prev, items: [...prev.items, statusEvent] }));
      }

      const note = roadTransitionNote(nextStatus, hold.holdReason, hold.holdNote);
      await recordOperationEvent({
        eventType,
        timestamp: at,
        locationId: call.locationId || DEFAULT_LOCATION_ID,
        assetId: call.assetId || null,
        workOrderId: call.workOrderId || call.id,
        technicianId: call.assignedTechnicianId || null,
        userId: null,
        previousStatus: call.status,
        newStatus: canonicalRoadStatus(nextStatus),
        note,
        relatedPhotoIds: [],
        metadata: JSON.stringify({ source: 'road-service-dispatch', roadServiceCallId: call.id }),
      });
      setEventsByCall(prev => ({
        ...prev,
        [call.id]: [{ eventType, timestamp: at, note, id: `local-${call.id}-${Date.now()}` }, ...(prev[call.id] || [])].slice(0, 5),
      }));
    } catch (err) {
      console.error('Road service status update failed:', err);
      alert('Could not update road service call.');
    } finally {
      setSaving(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="rs-view">
      {/* Dispatch toolbar — shared vtb pattern (same as the board views) */}
      <div className="vtb">
        <div className="vtb-left">
          <h1 className="rs-title">Road Service</h1>
          <div className="vtb-divider" />
          <div className="vtb-stat"><span className="vtb-stat-val">{activeCount}</span><span className="vtb-stat-lbl">Active</span></div>
          <div className="vtb-stat"><span className="vtb-stat-val">{incomingCount}</span><span className="vtb-stat-lbl">Unaccepted</span></div>
          <div className="vtb-stat"><span className="vtb-stat-val">{onHoldCount}</span><span className="vtb-stat-lbl">On Hold</span></div>
          <div className="vtb-stat"><span className="vtb-stat-val">{completedCount}</span><span className="vtb-stat-lbl">Completed</span></div>
        </div>
        <div className="vtb-right">
          {['active', 'closed', 'all'].map(f => (
            <button
              key={f}
              type="button"
              className={`rs-chip${filter === f ? ' rs-chip--active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
          <button type="button" className="rs-chip" onClick={() => { loadCalls(); loadTechLocations(); }}>Refresh</button>
          <FullscreenBtn />
          {canManage && (
            <button
              type="button"
              className="rs-new-btn"
              onClick={() => {
                // A stale edit/intake draft must never bleed into a new call
                setDraftCallId(null);
                setForm(EMPTY_CALL_FORM);
                setCreateOpen(true);
              }}
            >
              + New Call
            </button>
          )}
        </div>
      </div>

      {/* Dispatch console: docked call stack + full-bleed map */}
      <div className="rs-console">
        <aside className="rs-callstack">
          {loading && <div className="rs-empty">Loading road service calls…</div>}
          {!loading && visible.length === 0 && <div className="rs-empty">No calls in this view.</div>}
          {!loading && visible.map(call => (
            <CallCard
              key={call.id}
              call={call}
              tech={technicians.find(t => t.id === call.assignedTechnicianId)}
              events={eventsByCall[call.id] || []}
              saving={saving}
              geocoding={geocodingId === call.id}
              canManage={canManage}
              onStatusUpdate={requestStatusUpdate}
              onPinFromAddress={pinCallFromAddress}
              selected={selectedCall?.id === call.id}
              onSelect={() => setSelectedCall(call)}
              onConvert={() => setConvertCall(call)}
              onOpenRO={() => navigate(`/ro/${call.workOrderId}`)}
              technicians={technicians}
              onAssignTech={assignTech}
            />
          ))}
        </aside>
        <div className="rs-map-pane">
          <DispatchMap
            calls={calls}
            techLocations={techLocations}
            onSelectCall={setSelectedCall}
            selectedCallId={selectedCall?.id}
          />
        </div>
      </div>

      {/* New Call — slide-over panel */}
      {createOpen && (
      <div className="rs-create-overlay" onClick={() => { if (!saving) setCreateOpen(false); }}>
        <form className="rs-create" onClick={e => e.stopPropagation()} onSubmit={createCall}>
          <div className="rs-create-head">
            <h2>{draftCallId ? 'Edit Call' : 'New Call'}</h2>
            <button type="button" className="rs-create-close" onClick={() => setCreateOpen(false)} title="Close">✕</button>
          </div>
          <div className="rs-form-row rs-form-2col">
            <label>
              Customer
              <input
                value={form.customerName}
                onChange={e => setForm(prev => ({ ...prev, customerName: e.target.value }))}
                placeholder="Customer name"
              />
            </label>
            <label>
              Company
              <input
                value={form.companyName}
                onChange={e => setForm(prev => ({ ...prev, companyName: e.target.value }))}
                placeholder="Company name"
              />
            </label>
          </div>
          <label>
            Contact Name
            <input
              value={form.contactName}
              onChange={e => setForm(prev => ({ ...prev, contactName: e.target.value }))}
              placeholder="Person calling"
            />
          </label>
          <label>
            Caller Phone
            <div className="rs-phone-row">
              <input
                value={form.callerPhone}
                onChange={e => setForm(prev => ({ ...prev, callerPhone: e.target.value }))}
                placeholder="Customer cell number"
              />
              <button
                type="button"
                className="rs-location-request-btn"
                onClick={sendIntakeLocationRequest}
                disabled={requestingLocation || !form.callerPhone.trim() || !canManage}
              >
                {requestingLocation ? 'Sending...' : 'Text Link'}
              </button>
            </div>
          </label>
          {draftCallId && (
            <div className="rs-intake-state">
              Location request started. Keep filling out the call.
            </div>
          )}
          {linkIsLocal && (
            <div className="rs-intake-state rs-intake-state--warning">
              <strong>Location links are using this local computer address.</strong>
              <span>Paste the deployed REKON URL so customer links open outside this computer.</span>
              <div className="rs-public-url-row">
                <input
                  value={publicUrlDraft}
                  onChange={e => setPublicUrlDraft(e.target.value)}
                  placeholder="https://your-rekon-app.vercel.app"
                />
                <button type="button" onClick={savePublicUrl}>Use URL</button>
              </div>
              <small>Production fix: set REACT_APP_PUBLIC_APP_URL to this same URL in Vercel.</small>
            </div>
          )}
          <label>
            Callback Phone
            <input
              value={form.callbackPhone}
              onChange={e => setForm(prev => ({ ...prev, callbackPhone: e.target.value }))}
              placeholder="Leave blank to use caller phone"
            />
          </label>
          <label>
            Complaint
            <textarea
              value={form.complaint}
              onChange={e => setForm(prev => ({ ...prev, complaint: e.target.value }))}
              rows={3}
              placeholder="Describe the roadside issue"
            />
          </label>
          <div className="rs-form-row rs-form-2col">
            <label>
              Unit #
              <input
                value={form.unitNumber}
                onChange={e => setForm(prev => ({ ...prev, unitNumber: e.target.value }))}
                placeholder="Truck or trailer"
              />
            </label>
            <label>
              VIN
              <input
                value={form.vin}
                onChange={e => setForm(prev => ({ ...prev, vin: e.target.value }))}
                placeholder="Last 8 or full VIN"
              />
            </label>
            <label>
              Year
              <input
                value={form.vehicleYear}
                onChange={e => setForm(prev => ({ ...prev, vehicleYear: e.target.value }))}
                placeholder="2021"
              />
            </label>
            <label>
              Make
              <input
                value={form.vehicleMake}
                onChange={e => setForm(prev => ({ ...prev, vehicleMake: e.target.value }))}
                placeholder="Freightliner"
              />
            </label>
            <label>
              Model
              <input
                value={form.vehicleModel}
                onChange={e => setForm(prev => ({ ...prev, vehicleModel: e.target.value }))}
                placeholder="Cascadia"
              />
            </label>
            <label>
              Color
              <input
                value={form.vehicleColor}
                onChange={e => setForm(prev => ({ ...prev, vehicleColor: e.target.value }))}
                placeholder="White"
              />
            </label>
          </div>
          <label>
            Customer Location
            <input
              value={form.roadServiceAddress}
              onChange={e => setForm(prev => ({ ...prev, roadServiceAddress: e.target.value }))}
              placeholder="Full address, city, state"
            />
          </label>

          <div className="rs-coords">
            <span className="rs-coords-label">Exact coordinates, if dispatch has them</span>
            <div className="rs-coords-row">
              <input
                type="number"
                step="any"
                value={form.destinationLatitude}
                onChange={e => setForm(prev => ({ ...prev, destinationLatitude: e.target.value }))}
                placeholder="Latitude"
                className="rs-coord-input"
              />
              <input
                type="number"
                step="any"
                value={form.destinationLongitude}
                onChange={e => setForm(prev => ({ ...prev, destinationLongitude: e.target.value }))}
                placeholder="Longitude"
                className="rs-coord-input"
              />
            </div>
          </div>

          <div className="rs-form-row rs-form-2col">
            <label>
              Priority
              <select value={form.priority} onChange={e => setForm(prev => ({ ...prev, priority: e.target.value }))}>
                <option value="NORMAL">Normal</option>
                <option value="HIGH">High</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </label>
            <label>
              Technician
              <select value={form.assignedTechnicianId} onChange={e => setForm(prev => ({ ...prev, assignedTechnicianId: e.target.value }))}>
                <option value="">Unassigned</option>
                {technicians.filter(t => t.active !== false).map(tech => (
                  <option key={tech.id} value={tech.id}>{tech.name || `Tech ${tech.number}`}</option>
                ))}
              </select>
            </label>
          </div>

          <button
            type="submit"
            disabled={
              saving
              || !form.complaint.trim()
              || (!form.roadServiceAddress.trim() && (!form.destinationLatitude || !form.destinationLongitude) && !draftCallId)
              || !canManage
            }
          >
            {saving ? 'Saving…' : draftCallId ? 'Save Call Details' : 'Create Call'}
          </button>
          {!canManage && <p className="rs-permission-note">Your role can view road service but cannot dispatch calls.</p>}
        </form>
      </div>
      )}

      {/* Road call → RO conversion (prefilled New RO modal) */}
      {convertCall && (
        <NewROModal
          onClose={() => setConvertCall(null)}
          onSubmit={handleConvertSubmit}
          prefill={roPrefillFromCall(convertCall)}
        />
      )}

      {/* Selected call detail drawer (from map or call-stack click) */}
      {selectedCall && (
        <CallDetailDrawer
          call={selectedCall}
          tech={technicians.find(t => t.id === selectedCall.assignedTechnicianId)}
          events={eventsByCall[selectedCall.id] || []}
          statusEvents={statusEvents.callId === selectedCall.id ? statusEvents.items : []}
          saving={saving}
          geocoding={geocodingId === selectedCall.id}
          canManage={canManage}
          onStatusUpdate={requestStatusUpdate}
          onPinFromAddress={pinCallFromAddress}
          onConvert={() => setConvertCall(selectedCall)}
          onOpenRO={() => navigate(`/ro/${selectedCall.workOrderId}`)}
          technicians={technicians}
          onAssignTech={assignTech}
          onEdit={() => editCall(selectedCall)}
          onClose={() => setSelectedCall(null)}
        />
      )}

      {holdTarget && (
        <HoldReasonDialog
          call={holdTarget.call}
          saving={saving}
          onCancel={() => setHoldTarget(null)}
          onConfirm={({ holdReason, holdNote }) => {
            const { call, eventType } = holdTarget;
            setHoldTarget(null);
            updateCallStatus(call, ROAD_STATUS.HOLD, eventType, { holdReason, holdNote });
          }}
        />
      )}
    </div>
  );
}

// ── HoldReasonDialog ─────────────────────────────────────────────────────────
// A hold with no reason is a hole in the report and a phone call to the shop.
function HoldReasonDialog({ call, saving, onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const unit = call.unitNumber ? `Unit ${call.unitNumber}` : (call.companyName || call.customerName || '');

  return (
    <div className="rs-modal-backdrop" onClick={onCancel}>
      <div className="rs-hold-dialog" onClick={e => e.stopPropagation()}>
        <h3>Put this call on hold</h3>
        {unit && <p className="rs-hold-dialog-sub">{unit}</p>}

        <label htmlFor="rs-hold-reason">Reason</label>
        <select
          id="rs-hold-reason"
          value={reason}
          onChange={e => setReason(e.target.value)}
          autoFocus
        >
          <option value="">Select a reason…</option>
          {ROAD_HOLD_REASONS.map(option => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>

        <label htmlFor="rs-hold-note">Note (optional)</label>
        <textarea
          id="rs-hold-note"
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Part number, who you're waiting on, callback time"
          rows={3}
        />

        <div className="rs-hold-dialog-actions">
          <button type="button" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="rs-hold-btn"
            disabled={!reason || saving}
            onClick={() => onConfirm({ holdReason: reason, holdNote: note.trim() || null })}
          >
            Hold Call
          </button>
        </div>
      </div>
    </div>
  );
}

// ── CallCard ─────────────────────────────────────────────────────────────────
function TechSelect({ call, technicians, saving, onAssignTech }) {
  return (
    <select
      className="rs-tech-select"
      value={call.assignedTechnicianId || ''}
      disabled={saving}
      onClick={e => e.stopPropagation()}
      onChange={e => { e.stopPropagation(); onAssignTech(call, e.target.value); }}
      title="Assign technician"
    >
      <option value="">Unassigned</option>
      {technicians.filter(t => t.active !== false).map(t => (
        <option key={t.id} value={t.id}>{t.name || `Tech ${t.number}`}</option>
      ))}
    </select>
  );
}

function CallCard({ call, tech, events, saving, geocoding, canManage, onStatusUpdate, onPinFromAddress, selected, onSelect, onConvert, onOpenRO, technicians, onAssignTech }) {
  const navUrl = mapsUrl(call.destinationLatitude, call.destinationLongitude, call.roadServiceAddress);
  const isPinned = call.destinationLatitude != null && call.destinationLongitude != null;
  const vehicle = [call.vehicleYear, call.vehicleMake, call.vehicleModel, call.vehicleColor].filter(Boolean).join(' ');
  const customer = call.companyName || call.customerName || call.contactName;
  const cardEvents = events.slice(0, 5);

  return (
    <article
      className={`rs-card rs-card--${statusTone(call.status)}${selected ? ' rs-card--selected' : ''}`}
      onClick={onSelect}
    >
      <div className="rs-card-top">
        <span className={`rs-status rs-status--${statusTone(call.status)}`}>{statusLabel(call.status)}</span>
        <span className="rs-priority">{call.priority || 'NORMAL'}</span>
      </div>
      <h3>{call.complaint}</h3>
      {canonicalRoadStatus(call.status) === ROAD_STATUS.HOLD && (
        <p className="rs-hold-line">
          On hold — {roadHoldReasonLabel(call.currentHoldReason)}
          {call.currentHoldNote ? `: ${call.currentHoldNote}` : ''}
        </p>
      )}
      {customer && <p className="rs-customer-line">{customer}</p>}
      {vehicle && <p className="rs-vehicle-line">{vehicle}</p>}
      {(call.unitNumber || call.vin) && <p className="rs-vehicle-line">{[call.unitNumber && `Unit ${call.unitNumber}`, call.vin && `VIN ${call.vin}`].filter(Boolean).join(' | ')}</p>}
      <p className="rs-address">{call.roadServiceAddress || 'No address'}</p>

      {!isPinned && (
        <div className="rs-pin-warning">
          <span>Not pinned on map</span>
          {call.roadServiceAddress && canManage && (
            <button type="button" onClick={() => onPinFromAddress(call)} disabled={geocoding || saving}>
              {geocoding ? 'Pinning...' : 'Pin from address'}
            </button>
          )}
        </div>
      )}

      {navUrl && (
        <a href={navUrl} target="_blank" rel="noreferrer" className="rs-nav-btn">
          Navigate ↗
        </a>
      )}

      {call.locationRequestStatus && (
        <div className="rs-location-request-state">
          Location request: {call.locationRequestStatus.toLowerCase()}
        </div>
      )}

      <div className="rs-meta">
        {canManage ? (
          <TechSelect call={call} technicians={technicians} saving={saving} onAssignTech={onAssignTech} />
        ) : (
          <span>{tech ? tech.name : 'Unassigned'}</span>
        )}
        <span>{formatTime(call.updatedAt || call.createdAt)}</span>
      </div>

      <div className="rs-actions">
        {dispatchTransitions(call).map(next => (
          <button
            key={next}
            type="button"
            className={next === ROAD_STATUS.HOLD ? 'rs-hold-btn' : undefined}
            onClick={(e) => { e.stopPropagation(); onStatusUpdate(call, next, statusEventType(next)); }}
            disabled={saving || !canManage}
          >
            {dispatchTransitionLabel(call, next)}
          </button>
        ))}
        {call.workOrderId ? (
          <button type="button" className="rs-ro-link" onClick={(e) => { e.stopPropagation(); onOpenRO(); }} title="Open the repair order created from this call">
            RO ✓ Open
          </button>
        ) : (
          canManage && call.status !== 'CANCELLED' && (
            <button type="button" className="rs-convert" onClick={(e) => { e.stopPropagation(); onConvert(); }} disabled={saving} title="Create a repair order from this call">
              → Create RO
            </button>
          )
        )}
        {isRoadCallActive(call) && (
          <button type="button" className="danger" onClick={(e) => { e.stopPropagation(); onStatusUpdate(call, ROAD_STATUS.CANCELLED, EVENT_TYPES.CANCELLED); }} disabled={saving || !canManage}>
            Cancel
          </button>
        )}
      </div>

      <div className="rs-activity">
        <span className="rs-activity-title">Activity</span>
        {cardEvents.length === 0 ? <span className="rs-activity-empty">No events yet</span> : cardEvents.map(ev => (
          <div key={ev.id} className="rs-activity-row">
            <span>{ev.eventType.replace(/_/g, ' ')}</span>
            <time>{formatTime(ev.timestamp)}</time>
            {ev.note && <small>{ev.note}</small>}
          </div>
        ))}
      </div>
    </article>
  );
}

// ── CallDetailDrawer (shown when map pin is clicked) ─────────────────────────
function CallDetailDrawer({ call, tech, events, statusEvents, saving, geocoding, canManage, onStatusUpdate, onPinFromAddress, onConvert, onOpenRO, technicians, onAssignTech, onEdit, onClose }) {
  const navUrl = mapsUrl(call.destinationLatitude, call.destinationLongitude, call.roadServiceAddress);
  const isPinned = call.destinationLatitude != null && call.destinationLongitude != null;
  const vehicle = [call.vehicleYear, call.vehicleMake, call.vehicleModel, call.vehicleColor].filter(Boolean).join(' ');
  const onHold = canonicalRoadStatus(call.status) === ROAD_STATUS.HOLD;
  const response = summarizeRoadResponse(statusEvents, call);

  return (
    <div className="rs-drawer">
      <div className="rs-drawer-header">
        <div>
          <span className={`rs-status rs-status--${statusTone(call.status)}`}>{statusLabel(call.status)}</span>
          <h3>{call.complaint}</h3>
        </div>
        <button type="button" className="rs-drawer-close" onClick={onClose}>✕</button>
      </div>

      <div className="rs-drawer-body">
        {onHold && (
          <div className="rs-hold-banner">
            <strong>On hold — {roadHoldReasonLabel(call.currentHoldReason)}</strong>
            {call.currentHoldNote && <span>{call.currentHoldNote}</span>}
            {call.holdStartedAt && <time>since {formatTime(call.holdStartedAt)}</time>}
          </div>
        )}
        {(call.customerName || call.companyName || call.contactName || call.callbackPhone) && (
          <div className="rs-drawer-meta rs-drawer-meta--compact">
            {call.customerName && <div><strong>Customer</strong><span>{call.customerName}</span></div>}
            {call.companyName && <div><strong>Company</strong><span>{call.companyName}</span></div>}
            {call.contactName && <div><strong>Contact</strong><span>{call.contactName}</span></div>}
            {call.callbackPhone && <div><strong>Callback</strong><span>{call.callbackPhone}</span></div>}
          </div>
        )}
        <p className="rs-address">{call.roadServiceAddress || 'No address'}</p>
        {vehicle && <p className="rs-vehicle-line">{vehicle}</p>}
        {(call.unitNumber || call.vin) && <p className="rs-vehicle-line">{[call.unitNumber && `Unit ${call.unitNumber}`, call.vin && `VIN ${call.vin}`].filter(Boolean).join(' | ')}</p>}

        {!isPinned && (
          <div className="rs-pin-warning">
            <span>Not pinned on map</span>
            {call.roadServiceAddress && canManage && (
              <button type="button" onClick={() => onPinFromAddress(call)} disabled={geocoding || saving}>
                {geocoding ? 'Pinning...' : 'Pin from address'}
              </button>
            )}
          </div>
        )}

        {(call.destinationLatitude && call.destinationLongitude) && (
          <p className="rs-coords-display">
            {call.destinationLatitude.toFixed(5)}, {call.destinationLongitude.toFixed(5)}
          </p>
        )}

        {navUrl && (
          <a href={navUrl} target="_blank" rel="noreferrer" className="rs-nav-btn rs-nav-btn--full">
            Open in Google Maps ↗
          </a>
        )}

        {call.locationRequestStatus && (
          <div className="rs-location-request-state">
            Location request: {call.locationRequestStatus.toLowerCase()}
          </div>
        )}

        <div className="rs-drawer-meta">
          <div>
            <strong>Technician</strong>
            {canManage ? (
              <TechSelect call={call} technicians={technicians} saving={saving} onAssignTech={onAssignTech} />
            ) : (
              <span>{tech ? tech.name : 'Unassigned'}</span>
            )}
          </div>
          <div><strong>Priority</strong><span>{call.priority || 'NORMAL'}</span></div>
          {call.acceptedAt && <div><strong>Accepted</strong><span>{formatTime(call.acceptedAt)}</span></div>}
          {call.travelStartedAt && <div><strong>En Route</strong><span>{formatTime(call.travelStartedAt)}</span></div>}
          {call.arrivedAt && <div><strong>On Scene</strong><span>{formatTime(call.arrivedAt)}</span></div>}
          {call.completedAt && <div><strong>Completed</strong><span>{formatTime(call.completedAt)}</span></div>}
        </div>

        {/* Response time — the numbers customers and Decisiv ask for, read off
            the transition history rather than guessed from the current status. */}
        <div className="rs-response">
          <span className="rs-response-title">Response Time</span>
          <div className="rs-response-legs">
            <div><strong>Acknowledge</strong><span>{formatDuration(response.acceptSeconds)}</span></div>
            <div><strong>Roll Out</strong><span>{formatDuration(response.rollSeconds)}</span></div>
            <div><strong>Drive</strong><span>{formatDuration(response.driveSeconds)}</span></div>
            <div><strong>On Scene</strong><span>{formatDuration(response.onSceneSeconds)}</span></div>
            {response.holdSeconds > 0 && <div><strong>Held</strong><span>{formatDuration(response.holdSeconds)}</span></div>}
            <div><strong>Total</strong><span>{formatDuration(response.totalSeconds)}</span></div>
          </div>
        </div>

        {statusEvents.length > 0 && (
          <div className="rs-activity">
            <span className="rs-activity-title">Status History</span>
            {statusEvents.map(ev => (
              <div key={ev.id} className="rs-activity-row">
                <span>
                  {statusLabel(ev.status)}
                  {ev.holdReason ? ` — ${roadHoldReasonLabel(ev.holdReason)}` : ''}
                </span>
                <time>{formatTime(ev.at)}</time>
                {ev.holdNote && <small>{ev.holdNote}</small>}
                {ev.techName && <small>{ev.techName}</small>}
              </div>
            ))}
          </div>
        )}

        <div className="rs-drawer-actions">
          {dispatchTransitions(call).map(next => (
            <button
              key={next}
              type="button"
              className={next === ROAD_STATUS.HOLD ? 'rs-hold-btn' : undefined}
              onClick={() => onStatusUpdate(call, next, statusEventType(next))}
              disabled={saving || !canManage}
            >
              {dispatchTransitionLabel(call, next)}
            </button>
          ))}
          {canManage && isRoadCallActive(call) && (
            <button type="button" onClick={onEdit} disabled={saving} title="Edit call details">
              ✎ Edit Call
            </button>
          )}
          {call.workOrderId ? (
            <button type="button" className="rs-ro-link" onClick={onOpenRO} title="Open the repair order created from this call">
              RO ✓ Open
            </button>
          ) : (
            canManage && call.status !== 'CANCELLED' && (
              <button type="button" className="rs-convert" onClick={onConvert} disabled={saving} title="Create a repair order from this call">
                → Create RO
              </button>
            )
          )}
          {isRoadCallActive(call) && (
            <button type="button" className="danger" onClick={() => onStatusUpdate(call, ROAD_STATUS.CANCELLED, EVENT_TYPES.CANCELLED)} disabled={saving || !canManage}>
              Cancel
            </button>
          )}
        </div>

        {events.length > 0 && (
          <div className="rs-activity">
            <span className="rs-activity-title">Activity</span>
            {events.map(ev => (
              <div key={ev.id} className="rs-activity-row">
                <span>{ev.eventType.replace(/_/g, ' ')}</span>
                <time>{formatTime(ev.timestamp)}</time>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
